import { orderBy } from "lodash";
import { ProcessTransactions } from "../../../../../model/ProcessTransaction";
import {
  DetailTimelineDataItem,
  ProcessTimelineItemSourceInitializeArgs,
  TimelineItemSource,
} from "../../sources";
import { IPromise } from "angular";
import { ServerUtility } from "../../../../../../records/server/serverUtility";
import { TimelineGroup } from "vis-timeline";
import {
  ProcessInformation,
  ProcessStateMetadataInformation,
} from "../../../../../model/ProcessGroups";
import { ProcessApiResponse } from "../../../../../model/ProcessApi";

import { getComponentName } from "../detailComponent";

export const vsComponentName = getComponentName("stateInterval");

type TimelineStateInterval = DetailTimelineDataItem<StateIntervalTimelineData>;

export interface StateIntervalTimelineData {
  startEvent: ProcessTransactions;
  endEvent?: ProcessTransactions;
}

export interface ProcessTimelineGroup extends TimelineGroup {
  state: ProcessStateMetadataInformation;
  treeLevel: number;
}

export interface ProcessTimelineArgs extends ProcessTimelineItemSourceInitializeArgs<ProcessTimelineGroup> {
  process: ProcessApiResponse<unknown>;
  processInformation: ProcessInformation;
}

export class ProcessStateIntervalItemSource extends TimelineItemSource<
  ProcessTimelineGroup,
  ProcessTimelineArgs,
  StateIntervalTimelineData,
  TimelineStateInterval
> {
  constructor(private readonly serverUtility: ServerUtility) {
    super();
  }

  getSourceItems(args: ProcessTimelineArgs): IPromise<TimelineStateInterval[]> {
    const stateMap = new Map(
      args.processInformation.States.map((s) => [s.Id, s]),
    );

    return this.getStateTransactions(args.process.Id)
      .then((transactions) =>
        this.createTimelineIntervals(
          transactions.filter(
            (t) =>
              t.Type === "StateMachine" &&
              t.EventType &&
              ["StateEnter", "StateExit"].includes(t.EventType) &&
              (t.EventType === "StateEnter" || t.NewValue !== "initial"),
          ),
          args.process.CompletedTime ?? new Date(),
          stateMap,
        ),
      )
      .then((t) => {
        this.styleItems(t, args.groups);
        return t;
      });
  }

  protected override createTimelineDataItem(args: {
    group: string;
    start: Date;
    end?: Date;
    content: string;
    data: StateIntervalTimelineData;
  }): TimelineStateInterval {
    return {
      ...super.defaultTimelineItem(args),
      component: vsComponentName,
    };
  }

  private createTimelineIntervals(
    stateChanges: ProcessTransactions[],
    endTime: Date | undefined,
    stateMap: Map<string, ProcessStateMetadataInformation>,
  ): TimelineStateInterval[] {
    const stateTransactionsByState = orderBy(
      stateChanges,
      (st) => st.Time,
    ).reduce<Record<string, ProcessTransactions[]>>((result, current) => {
      const stateTransactions = (result[current.NewValue!] ??= []);
      stateTransactions.push(current);
      return result;
    }, {});

    return Object.entries(stateTransactionsByState).reduce<
      TimelineStateInterval[]
    >(
      (result, [stateId, events]) => {
        let startTransaction: ProcessTransactions | undefined;
        for (const currentTransaction of events) {
          if (currentTransaction.EventType === "StateEnter") {
            const stateId = currentTransaction.NewValue!;
            if (startTransaction !== undefined) {
              console.warn(
                `${stateId}: Enter at ${currentTransaction.Time?.toISOString()} while already open since ${startTransaction.Time?.toISOString()}`,
              );
              result.push(
                this.createTimelineDataItem({
                  group: stateId,
                  start: startTransaction.Time!,
                  content: stateMap.get(stateId)?.Name ?? stateId,
                  end: currentTransaction.Time,
                  data: {
                    startEvent: startTransaction,
                    endEvent: currentTransaction,
                  },
                }),
              ); // close at re-entry, or choose to drop
            }
            startTransaction = currentTransaction;
          } else if (currentTransaction.EventType === "StateExit") {
            if (startTransaction === undefined) {
              // Exit with no open Enter: orphan (log started mid-state, or data loss)
              console.warn(
                `${stateId}: orphan Exit at ${currentTransaction.Time?.toISOString()}`,
              );
              continue; // or: intervals.push({ stateId, start: logStart, end: ev.time })
            }
            result.push(
              this.createTimelineDataItem({
                group: stateId,
                start: startTransaction.Time!,
                content: stateMap.get(stateId)?.Name ?? stateId,
                end: currentTransaction.Time,
                data: {
                  startEvent: startTransaction,
                  endEvent: currentTransaction,
                },
              }),
            );
            startTransaction = undefined;
          } else {
            continue;
          }
        }

        if (startTransaction !== undefined) {
          result.push(
            this.createTimelineDataItem({
              group: stateId,
              start: startTransaction.Time!,
              content: stateMap.get(stateId)?.Name ?? stateId,
              end: endTime,
              data: {
                startEvent: startTransaction,
                endEvent: undefined,
              },
            }),
          );
        }

        return result;
      },
      <TimelineStateInterval[]>[],
    );
  }

  private getStateTransactions(processInstancesId: number) {
    return this.serverUtility.fetchAll<ProcessTransactions>({
      url: "/api/processes/transactions",
      query: {
        ProcessInstancesId: processInstancesId,
        Type: "StateMachine",
      },
    });
  }
}
